import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Plus, Trash2, FileDown, CalendarDays, Users, CheckCircle2, Loader2, Plane, Palmtree, AlertTriangle } from "lucide-react";

const euro = (v) => `€ ${Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const thisMonth = () => new Date().toISOString().slice(0, 7);
const MESI = ["", "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const GIORNI = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const EMPTY = { data: "", tipo: "lavoro", ore: "", destinazione: "", km: "", rimborso_km: "", rimborso_pasti: "", rimborso_trasporto: "", rimborso_altro: "", note: "" };

function daysInMonth(mese) {
  const [y, m] = mese.split("-").map(Number);
  return new Date(y, m, 0).getDate();
}
function firstWeekday(mese) {
  const [y, m] = mese.split("-").map(Number);
  const wd = new Date(y, m - 1, 1).getDay(); // 0=dom
  return (wd + 6) % 7; // 0=lun
}
function shiftMonth(mese, delta) {
  const [y, m] = mese.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function GiornaliereCalendar({ persone = [], selfMode = false }) {
  const [meta, setMeta] = useState(null);
  const [mese, setMese] = useState(thisMonth());
  const [personaId, setPersonaId] = useState("");
  const [view, setView] = useState("persona"); // persona | generale
  const [entries, setEntries] = useState([]);
  const [allEntries, setAllEntries] = useState([]);
  const [ferie, setFerie] = useState(null);
  const [ferieCfg, setFerieCfg] = useState({ ferie_annue: "", residuo_precedente: "" });
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [wordDlg, setWordDlg] = useState(null); // { nBozza }
  const [kmTariffaEdit, setKmTariffaEdit] = useState("");

  useEffect(() => { if (meta?.km_tariffa != null) setKmTariffaEdit(String(meta.km_tariffa)); }, [meta]);
  const saveKmTariffa = async () => {
    try {
      const { data } = await api.put("/personale/calendario/km-tariffa", { tariffa: kmTariffaEdit });
      setMeta((m) => ({ ...m, km_tariffa: data.km_tariffa }));
      toast.success(`Tariffa aggiornata: € ${data.km_tariffa}/km`);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const tipoMap = useMemo(() => {
    const m = {};
    (meta?.tipi || []).forEach((t) => { m[t.code] = t; });
    return m;
  }, [meta]);

  useEffect(() => {
    api.get("/personale/calendario/tipi").then(({ data }) => {
      setMeta(data);
      if (selfMode && data.my_persona_id) setPersonaId(data.my_persona_id);
      else if (!selfMode && persone.length) setPersonaId(persone[0].id);
    }).catch(() => {});   // caricamento tipi: errore non-bloccante, nessun toast disturbante
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selfMode, persone.length]);

  const canGeneral = meta?.vede_generale;
  const isManager = meta?.is_manager;

  const loadEntries = useCallback(async () => {
    if (view === "generale" && canGeneral) {
      try {
        const { data } = await api.get("/personale/calendario", { params: { mese } });
        setAllEntries(data.entries);
      } catch (e) { setAllEntries([]); }   // caricamento: fail silenzioso
      return;
    }
    if (!personaId) return;
    try {
      const { data } = await api.get("/personale/calendario", { params: { mese, persona_id: personaId } });
      setEntries(data.entries);
    } catch (e) { setEntries([]); }   // caricamento attività: fail silenzioso
    try {
      const { data: f } = await api.get("/personale/calendario/ferie", { params: { anno: Number(mese.slice(0, 4)), mese: Number(mese.slice(5, 7)), persona_id: personaId } });
      const fp = f.ferie[0] || null;
      setFerie(fp);
      if (fp) setFerieCfg({ ferie_annue: String(fp.ferie_annue), residuo_precedente: String(fp.residuo_precedente) });
    } catch (e) { /* ferie non disponibili: non blocca la pagina */ }
  }, [mese, personaId, view, canGeneral]);

  useEffect(() => { if (meta) loadEntries(); }, [meta, mese, personaId, view, loadEntries]);

  const byDay = useMemo(() => {
    const m = {};
    entries.forEach((e) => { const d = Number(e.data.slice(8, 10)); (m[d] = m[d] || []).push(e); });
    return m;
  }, [entries]);

  const openNew = (day) => {
    if (selfMode && personaId !== meta?.my_persona_id) return;
    const dd = `${mese}-${String(day).padStart(2, "0")}`;
    setForm({ ...EMPTY, data: dd }); setEditId(null); setDlg(true);
  };
  const openEdit = (e) => {
    if (!isManager && e.stato === "confermata") { toast.info("Attività già validata dall'amministrazione."); return; }
    setForm({ data: e.data, tipo: e.tipo, ore: e.ore || "", destinazione: e.destinazione || "", km: e.km || "",
      rimborso_km: e.rimborso_km || "", rimborso_pasti: e.rimborso_pasti || "", rimborso_trasporto: e.rimborso_trasporto || "",
      rimborso_altro: e.rimborso_altro || "", note: e.note || "" });
    setEditId(e.id); setDlg(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form, persona_id: personaId, ore: parseFloat(form.ore || 0), km: parseFloat(form.km || 0),
        rimborso_km: parseFloat(form.rimborso_km || 0), rimborso_pasti: parseFloat(form.rimborso_pasti || 0),
        rimborso_trasporto: parseFloat(form.rimborso_trasporto || 0), rimborso_altro: parseFloat(form.rimborso_altro || 0) };
      if (editId) await api.put(`/personale/calendario/${editId}`, body);
      else await api.post("/personale/calendario", body);
      toast.success(editId ? "Attività aggiornata" : "Attività registrata");
      setDlg(false); loadEntries();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async () => {
    if (!editId) return;
    try { await api.delete(`/personale/calendario/${editId}`); setDlg(false); loadEntries(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const conferma = async (e, riapri) => {
    try { await api.post(`/personale/calendario/${e.id}/conferma`, { riapri }); loadEntries(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const salvaFerieCfg = async () => {
    setBusy(true);
    try {
      await api.post("/personale/calendario/ferie-config", { persona_id: personaId, anno: Number(mese.slice(0, 4)),
        ferie_annue: parseFloat(ferieCfg.ferie_annue || 0), residuo_precedente: parseFloat(ferieCfg.residuo_precedente || 0) });
      toast.success("Ferie aggiornate"); loadEntries();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const validaMese = async () => {
    const nBozza = entries.filter((e) => e.stato === "bozza").length;
    if (!window.confirm(`Validare tutte le giornaliere in bozza di ${MESI[Number(mese.slice(5, 7))]} ${mese.slice(0, 4)}${view === "persona" ? " per questo collaboratore" : ""}?`)) return;
    setBusy(true);
    try {
      const body = { mese };
      if (view === "persona" && personaId) body.persona_id = personaId;
      const { data } = await api.post("/personale/calendario/valida-mese", body);
      toast.success(`${data.validate} giornaliere validate`); loadEntries();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const downloadWord = async () => {
    try {
      const { data: blob } = await api.get("/personale/calendario/export-word", { params: { mese }, responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `giornaliere_${mese}.docx`; a.click();
    } catch { toast.error("Errore export Word"); }
  };

  const exportWord = async () => {
    try {
      const { data } = await api.get("/personale/calendario", { params: { mese } });
      const nBozza = (data.entries || []).filter((e) => e.stato === "bozza").length;
      if (nBozza > 0) { setWordDlg({ nBozza }); return; }
      await downloadWord();
    } catch { await downloadWord(); }
  };

  const validaEScarica = async () => {
    setBusy(true);
    try {
      await api.post("/personale/calendario/valida-mese", { mese });
      await downloadWord();
      setWordDlg(null); loadEntries();
      toast.success("Giornaliere validate e report scaricato");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!meta) return <div className="p-10 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;

  const ndays = daysInMonth(mese);
  const lead = firstWeekday(mese);
  const rp = ferie; // ferie object holds nome too

  return (
    <div data-testid="giornaliere-calendar">
      {/* Barra controlli */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white">
          <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => setMese(shiftMonth(mese, -1))} data-testid="gio-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="px-2 text-sm font-semibold min-w-[130px] text-center" data-testid="gio-mese">{MESI[Number(mese.slice(5, 7))]} {mese.slice(0, 4)}</span>
          <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => setMese(shiftMonth(mese, 1))} data-testid="gio-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
        {!selfMode && view === "persona" && (
          <select value={personaId} onChange={(e) => setPersonaId(e.target.value)} className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm" data-testid="gio-persona">
            {persone.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
          </select>
        )}
        {canGeneral && (
          <div className="flex rounded-lg border border-slate-200 overflow-hidden">
            <button onClick={() => setView("persona")} className={`px-3 h-9 text-sm ${view === "persona" ? "bg-blue-600 text-white" : "bg-white text-slate-600"}`} data-testid="gio-view-persona"><CalendarDays className="h-4 w-4 inline mr-1" />Collaboratore</button>
            <button onClick={() => setView("generale")} className={`px-3 h-9 text-sm ${view === "generale" ? "bg-blue-600 text-white" : "bg-white text-slate-600"}`} data-testid="gio-view-generale"><Users className="h-4 w-4 inline mr-1" />Generale</button>
          </div>
        )}
        <div className="flex-1" />
        {isManager && entries.some((e) => e.stato === "bozza") && view === "persona" && (
          <Button variant="outline" className="text-emerald-600 border-emerald-300" onClick={validaMese} disabled={busy} data-testid="gio-valida-mese"><CheckCircle2 className="h-4 w-4 mr-1" /> Valida tutto il mese</Button>
        )}
        {isManager && <Button variant="outline" onClick={exportWord} data-testid="gio-export-word"><FileDown className="h-4 w-4 mr-1" /> Export Word (Studio Corrias)</Button>}
      </div>

      {view === "generale" && canGeneral ? (
        <GeneraleGrid mese={mese} ndays={ndays} persone={persone} allEntries={allEntries} tipoMap={tipoMap} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Calendario */}
          <div className="lg:col-span-3 rounded-xl border border-slate-200 bg-white p-3">
            <div className="grid grid-cols-7 gap-1 mb-1">
              {GIORNI.map((g) => <div key={g} className="text-center text-xs font-semibold text-slate-400 py-1">{g}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: lead }).map((_, i) => <div key={`b${i}`} />)}
              {Array.from({ length: ndays }).map((_, i) => {
                const day = i + 1;
                const evs = byDay[day] || [];
                return (
                  <div key={day} onClick={() => openNew(day)} className="min-h-[74px] rounded-lg border border-slate-100 hover:border-blue-300 hover:bg-blue-50/40 p-1 cursor-pointer transition-colors" data-testid={`gio-day-${day}`}>
                    <div className="text-[11px] text-slate-400 font-medium">{day}</div>
                    <div className="space-y-0.5 mt-0.5">
                      {evs.map((e) => (
                        <div key={e.id} onClick={(ev) => { ev.stopPropagation(); openEdit(e); }}
                          className={`text-[10px] rounded px-1 py-0.5 truncate ${e.stato === "bozza" ? "ring-1 ring-dashed ring-amber-400" : ""}`}
                          style={{ backgroundColor: tipoMap[e.tipo]?.color || "#e2e8f0" }} title={tipoMap[e.tipo]?.label}>
                          {tipoMap[e.tipo]?.label?.split(" ")[0] || e.tipo}{e.ore ? ` ${e.ore}h` : ""}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Pannello laterale: riepilogo + ferie */}
          <div className="space-y-4">
            <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="gio-ferie">
              <div className="flex items-center gap-2 text-slate-700 mb-2"><Palmtree className="h-4 w-4 text-emerald-600" /><h3 className="font-heading text-sm font-semibold">Ferie {mese.slice(0, 4)}</h3></div>
              {rp && (
                <div className="grid grid-cols-3 gap-2 text-center mb-3">
                  <div className="rounded-lg bg-slate-50 p-2"><div className="text-[10px] uppercase text-slate-400">Maturate</div><div className="font-bold text-slate-800">{rp.maturate}</div></div>
                  <div className="rounded-lg bg-slate-50 p-2"><div className="text-[10px] uppercase text-slate-400">Consumate</div><div className="font-bold text-amber-600">{rp.consumate}</div></div>
                  <div className="rounded-lg bg-emerald-50 p-2"><div className="text-[10px] uppercase text-emerald-500">Residue</div><div className="font-bold text-emerald-700">{rp.residue}</div></div>
                </div>
              )}
              {isManager && (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-[11px]">Ferie annue</Label><Input type="number" step="0.5" value={ferieCfg.ferie_annue} onChange={(e) => setFerieCfg({ ...ferieCfg, ferie_annue: e.target.value })} className="h-8" data-testid="gio-ferie-annue" /></div>
                    <div><Label className="text-[11px]">Residuo anno prec.</Label><Input type="number" step="0.5" value={ferieCfg.residuo_precedente} onChange={(e) => setFerieCfg({ ...ferieCfg, residuo_precedente: e.target.value })} className="h-8" data-testid="gio-ferie-residuo" /></div>
                  </div>
                  <Button size="sm" onClick={salvaFerieCfg} disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="gio-ferie-save">Salva ferie</Button>
                </div>
              )}
            </div>

            <Riepilogo entries={entries} tipoMap={tipoMap} />

            {isManager && entries.some((e) => e.stato === "bozza") && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700">
                {entries.filter((e) => e.stato === "bozza").length} attività in bozza da validare. Apri un'attività tratteggiata per confermarla.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Legenda */}
      <div className="flex flex-wrap gap-2 mt-4">
        {(meta.tipi || []).map((t) => (
          <span key={t.code} className="inline-flex items-center gap-1 text-[11px] text-slate-500">
            <span className="h-3 w-3 rounded" style={{ backgroundColor: t.color }} />{t.label}
          </span>
        ))}
      </div>

      {/* Dialog attività */}
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Modifica attività" : "Nuova attività"} · {form.data}</DialogTitle>
            <DialogDescription>Registra ferie, trasferte, corsi, permessi e rimborsi per la giornata.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="col-span-2 space-y-1"><Label className="text-xs">Tipo attività</Label>
              <select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })} className="w-full h-10 rounded-md border border-slate-300 px-2 text-sm" data-testid="gio-f-tipo">
                {(meta.tipi || []).map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
              </select></div>
            <div className="space-y-1"><Label className="text-xs">Ore (permessi/corsi)</Label><Input type="number" step="0.5" value={form.ore} onChange={(e) => setForm({ ...form, ore: e.target.value })} data-testid="gio-f-ore" /></div>
            <div className="space-y-1"><Label className="text-xs">Destinazione</Label><Input value={form.destinazione} onChange={(e) => setForm({ ...form, destinazione: e.target.value })} data-testid="gio-f-dest" /></div>
            <div className="space-y-1"><Label className="text-xs">KM</Label><Input type="number" step="0.1" value={form.km} onChange={(e) => { const v = e.target.value; const t = meta.km_tariffa || 0.35; setForm((f) => ({ ...f, km: v, rimborso_km: v === "" ? "" : (parseFloat(v || 0) * t).toFixed(2) })); }} data-testid="gio-f-km" /></div>
            <div className="space-y-1"><Label className="text-xs">Rimborso KM € <span className="text-[10px] text-slate-400">(auto €{meta.km_tariffa || 0.35}/km)</span></Label><Input type="number" step="0.01" value={form.rimborso_km} onChange={(e) => setForm({ ...form, rimborso_km: e.target.value })} data-testid="gio-f-rkm" /></div>
            <div className="space-y-1"><Label className="text-xs">Rimborso pasti €</Label><Input type="number" step="0.01" value={form.rimborso_pasti} onChange={(e) => setForm({ ...form, rimborso_pasti: e.target.value })} data-testid="gio-f-rpasti" /></div>
            <div className="space-y-1"><Label className="text-xs">Rimborso trasporto €</Label><Input type="number" step="0.01" value={form.rimborso_trasporto} onChange={(e) => setForm({ ...form, rimborso_trasporto: e.target.value })} data-testid="gio-f-rtrasp" /></div>
            <div className="space-y-1"><Label className="text-xs">Rimborso altro €</Label><Input type="number" step="0.01" value={form.rimborso_altro} onChange={(e) => setForm({ ...form, rimborso_altro: e.target.value })} data-testid="gio-f-raltro" /></div>
            <div className="col-span-2 space-y-1"><Label className="text-xs">Note</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="gio-f-note" /></div>
            {meta.is_manager && (
              <div className="col-span-2 flex items-end gap-2 bg-slate-50 rounded-md p-2 mt-1">
                <div className="space-y-1"><Label className="text-[11px] text-slate-500">Tariffa €/km (calcolo automatico rimborso)</Label>
                  <Input type="number" step="0.01" value={kmTariffaEdit} onChange={(e) => setKmTariffaEdit(e.target.value)} className="h-8 w-28" data-testid="gio-f-km-tariffa" /></div>
                <Button type="button" size="sm" variant="outline" className="h-8" onClick={saveKmTariffa} data-testid="gio-f-km-tariffa-save">Salva tariffa</Button>
              </div>
            )}
          </div>
          <DialogFooter className="flex-wrap gap-2">
            {editId && <Button variant="ghost" className="text-red-500 mr-auto" onClick={del} data-testid="gio-f-del"><Trash2 className="h-4 w-4 mr-1" /> Elimina</Button>}
            {editId && isManager && (
              (entries.find((x) => x.id === editId)?.stato === "bozza")
                ? <Button variant="outline" className="text-emerald-600" onClick={() => { conferma(entries.find((x) => x.id === editId), false); setDlg(false); }} data-testid="gio-f-conferma"><CheckCircle2 className="h-4 w-4 mr-1" /> Valida</Button>
                : <Button variant="outline" onClick={() => { conferma(entries.find((x) => x.id === editId), true); setDlg(false); }} data-testid="gio-f-riapri">Riporta in bozza</Button>
            )}
            <Button variant="outline" onClick={() => setDlg(false)}>Annulla</Button>
            <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="gio-f-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Avviso bozze non validate all'export Word */}
      <Dialog open={!!wordDlg} onOpenChange={(o) => !o && setWordDlg(null)}>
        <DialogContent className="max-w-md" data-testid="gio-word-dlg">
          <DialogHeader><DialogTitle className="font-heading">Giornaliere in bozza</DialogTitle>
            <DialogDescription>
              Ci sono <b>{wordDlg?.nBozza}</b> giornaliere ancora in bozza (non validate) nel mese {MESI[Number(mese.slice(5, 7))]} {mese.slice(0, 4)}.
              Il report per lo Studio Corrias dovrebbe contenere solo dati confermati.
            </DialogDescription></DialogHeader>
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="ghost" onClick={() => { setWordDlg(null); downloadWord(); }} data-testid="gio-word-scarica">Scarica comunque</Button>
            <Button variant="outline" onClick={() => setWordDlg(null)}>Annulla</Button>
            <Button onClick={validaEScarica} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="gio-word-valida">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1" /> Valida e scarica</>}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Riepilogo({ entries, tipoMap }) {
  const agg = useMemo(() => {
    let ferieD = 0, malD = 0, infD = 0, trasfD = 0, orePerm = 0, oreCorsi = 0, rimb = 0;
    entries.forEach((e) => {
      const cat = tipoMap[e.tipo]?.cat;
      if (cat === "ferie") ferieD++;
      if (cat === "malattia") malD++;
      if (cat === "infortunio") infD++;
      if (cat === "trasferta") trasfD++;
      if (cat === "permesso") orePerm += Number(e.ore || 0);
      if (cat === "formazione") oreCorsi += Number(e.ore || 0);
      rimb += Number(e.rimborso_km || 0) + Number(e.rimborso_pasti || 0) + Number(e.rimborso_trasporto || 0) + Number(e.rimborso_altro || 0);
    });
    return { ferieD, malD, infD, trasfD, orePerm, oreCorsi, rimb };
  }, [entries, tipoMap]);
  const Row = ({ l, v }) => <div className="flex justify-between text-sm py-0.5"><span className="text-slate-500">{l}</span><span className="font-semibold text-slate-800">{v}</span></div>;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="gio-riepilogo">
      <h3 className="font-heading text-sm font-semibold text-slate-700 mb-2">Riepilogo mese</h3>
      <Row l="Giorni ferie" v={agg.ferieD} />
      <Row l="Giorni malattia" v={agg.malD} />
      <Row l="Giorni infortunio" v={agg.infD} />
      <Row l="Giorni trasferta" v={agg.trasfD} />
      <Row l="Ore permessi/ROL" v={agg.orePerm} />
      <Row l="Ore corsi" v={agg.oreCorsi} />
      <div className="border-t mt-1 pt-1"><Row l="Totale rimborsi" v={euro(agg.rimb)} /></div>
    </div>
  );
}

function GeneraleGrid({ mese, ndays, persone, allEntries, tipoMap }) {
  const map = useMemo(() => {
    const m = {};
    allEntries.forEach((e) => { const k = `${e.persona_id}_${Number(e.data.slice(8, 10))}`; (m[k] = m[k] || []).push(e); });
    return m;
  }, [allEntries]);
  const countByPersona = useMemo(() => {
    const c = {};
    allEntries.forEach((e) => { c[e.persona_id] = (c[e.persona_id] || 0) + 1; });
    return c;
  }, [allEntries]);
  // Collaboratori attesi = quelli con account collegato (devono compilare la giornaliera).
  const attesi = persone.filter((p) => p.user_id);
  const list = attesi.length ? attesi : persone;
  const mancanti = list.filter((p) => !countByPersona[p.id]);
  return (
    <div>
      {mancanti.length > 0 ? (
        <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex items-start gap-2" data-testid="gio-gen-mancanti">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span><b>{mancanti.length}</b> {mancanti.length === 1 ? "collaboratore non ha" : "collaboratori non hanno"} ancora compilato la giornaliera di questo mese: <b>{mancanti.map((p) => p.nome).join(", ")}</b>. Sollecitali prima dell'export Word per lo Studio Corrias.</span>
        </div>
      ) : (list.length > 0 && (
        <div className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700 flex items-center gap-2" data-testid="gio-gen-tutti-ok">
          <CheckCircle2 className="h-4 w-4 shrink-0" /> Tutti i collaboratori hanno compilato almeno una giornaliera questo mese.
        </div>
      ))}
      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto" data-testid="gio-generale-grid">
        <table className="text-xs border-collapse w-full">
          <thead>
            <tr>
              <th className="sticky left-0 bg-slate-50 border-b border-r border-slate-200 px-2 py-1.5 text-left min-w-[160px]">Collaboratore</th>
              {Array.from({ length: ndays }).map((_, i) => <th key={i} className="border-b border-slate-100 px-1 py-1 text-center text-slate-400 font-medium min-w-[22px]">{i + 1}</th>)}
            </tr>
          </thead>
          <tbody>
            {list.map((p) => {
              const missing = !countByPersona[p.id];
              return (
                <tr key={p.id} data-testid={`gio-gen-row-${p.id}`} className={missing ? "bg-amber-50/60" : ""}>
                  <td className={`sticky left-0 border-r border-slate-200 px-2 py-1 font-medium whitespace-nowrap ${missing ? "bg-amber-50" : "bg-white"}`}>
                    <div className="flex items-center gap-1.5">
                      <span className={missing ? "text-amber-800" : "text-slate-700"}>{p.nome}</span>
                      {missing && <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-700 text-[9px] px-1.5 py-0.5 font-semibold" data-testid={`gio-gen-badge-${p.id}`}>Da compilare</span>}
                    </div>
                  </td>
                  {Array.from({ length: ndays }).map((_, i) => {
                    const evs = map[`${p.id}_${i + 1}`] || [];
                    const e = evs[0];
                    return (
                      <td key={i} className="border border-slate-50 p-0 text-center" title={e ? tipoMap[e.tipo]?.label : ""} style={{ backgroundColor: e ? (tipoMap[e.tipo]?.color || "") : "" }}>
                        <span className="text-[9px]">{e ? (tipoMap[e.tipo]?.label?.[0] || "") : ""}</span>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
